/**
 * Mail's full-results view (agora#269): every thread `search()` (RFC 8621
 * §4.4.1's free-text filter, through `@wtfalch/email/mailbox`) finds for a
 * query, drawn by `SearchResultsPage`. `Mail.tsx` renders this instead of
 * the mailbox list whenever `location.query` is set -- reached at
 * `/h/<agoraId>/mail/search/<query>`, which is what Enter opens from the
 * header search when nothing is highlighted (`search/SearchField.tsx`).
 *
 * Runs its own request rather than reusing the header panel's: the panel
 * caps at 20 rows (`search/model.ts`'s `DEFAULT_LIMIT`) and this wants
 * every match.
 */
import type { MailClient } from '@wtfalch/email/mailbox';
import { useEffect, useState } from 'react';
import { searchMail } from '../search/mail.ts';
import { mailErrorMessage } from '../search/model.ts';
import { SearchResultsPage } from '../search/SearchResultsPage.tsx';
import type { SearchRow } from '../search/types.ts';

const LIMIT = 50;

export function MailSearchResults({
  client,
  agoraId,
  query,
  onOpen,
}: {
  client: MailClient;
  agoraId: string;
  query: string;
  onOpen: (row: SearchRow) => void;
}) {
  const [rows, setRows] = useState<readonly SearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    searchMail(client, agoraId, query, { limit: LIMIT })
      .then((outcome) => {
        if (!live) return;
        setRows(outcome.rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(mailErrorMessage(err));
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client, agoraId, query]);

  return (
    <SearchResultsPage
      query={query}
      icon="mail"
      loading={loading}
      error={error}
      rows={rows}
      emptyLabel={`No mail found for “${query}”.`}
      onOpen={onOpen}
    />
  );
}
